import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { WidgetProps, WidgetRef } from '../type';
import { ChatMessageData, SystemMessageData } from '@/types';
import { sleep } from '@/lib/utils';

interface Meteor {
  x: number;
  y: number;
  length: number;
  speed: number;
  angle: number;
  alpha: number;
  update: () => void;
  draw: (ctx: CanvasRenderingContext2D) => void;
}

const playMeteorSound = (ctx: AudioContext) => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = 'triangle'; // Softer than sawtooth
  osc.frequency.setValueAtTime(2000, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.3); // descending tone

  gain.gain.setValueAtTime(0.02, ctx.currentTime); // subtle
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);

  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.start();
  osc.stop(ctx.currentTime + 0.3);
};

const MeteorShowerCanvas = forwardRef<WidgetRef, WidgetProps>(({ targetRef, isFullscreen }: WidgetProps, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const meteorsRef = useRef<Meteor[]>([]);
  const dimensions = useRef({ width: window.innerWidth, height: window.innerHeight });
  const audioCtxRef = useRef<AudioContext | null>(null);

  const animationFrameIdRef = useRef<number | null>(null);
  const isAnimatingRef = useRef(false);

  const createMeteor = (): Meteor => {
    const length = Math.random() * 80 + 40;
    const speed = Math.random() * 4 + 10;
    const angle = Math.PI / 3; // 45 degrees
    const alpha = 1;
    const x = Math.random() * window.innerWidth;
    const y = -50;
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    playMeteorSound(audioCtxRef.current);
    return {
      x,
      y,
      length,
      speed,
      angle,
      alpha,
      update() {
        this.x += Math.cos(this.angle) * this.speed;
        this.y += Math.sin(this.angle) * this.speed;
        this.alpha -= 0.002;
      },
      draw(ctx) {
        ctx.save();
        ctx.globalAlpha = this.alpha;
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(
          this.x - Math.cos(this.angle) * this.length,
          this.y - Math.sin(this.angle) * this.length
        );
        ctx.shadowBlur = 8;
        ctx.shadowColor = 'white';
        ctx.strokeStyle = `hsl(${Math.random() * 20 + 180}, 100%, 85%)`; // bluish-white tones

        ctx.stroke();
        ctx.restore();
      }
    };
  };

  useImperativeHandle(ref, () => ({
    onMessage: (options: { message: SystemMessageData | ChatMessageData, x?: number, y?: number }) => {
      function extractNumber(str: string) {
        const match = str.match(/\d+/); // matches the first sequence of digits
        if (match) {
          return parseInt(match[0], 10);
        }
        return 0;
      }
      let num = extractNumber(options.message.message);
      if (num == null || num < 10 || num > 100) {
        num = Math.floor(Math.random() * 20) + 10;
      }
      async function start() {
        for (let i = 0; i < num; i++) {
          await sleep(Math.random() * 100); // Delay between 0 and 100ms
          meteorsRef.current.push(createMeteor());
          startAnimation();
        }
      }
      setTimeout(start, 500);
    }
  }));

  const startAnimation = () => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;

    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      if (!isAnimatingRef.current) return;

      animationFrameIdRef.current = requestAnimationFrame(animate);

      const { width: W, height: H } = dimensions.current;
      ctx.clearRect(0, 0, W, H);

      meteorsRef.current.forEach(m => {
        m.update();
        m.draw(ctx);
      });

      meteorsRef.current = meteorsRef.current.filter(
        m => m.alpha > 0 && m.x < W && m.y < H
      );
      // optimization - stop when no meteors left
      if (meteorsRef.current.length === 0) {
        ctx.clearRect(0, 0, W, H);
        stopAnimation();
      }
    };

    animationFrameIdRef.current = requestAnimationFrame(animate);
  };

  const stopAnimation = () => {
    if (animationFrameIdRef.current !== null) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
    isAnimatingRef.current = false;
  };

  const getBounds = () => {
    const container = targetRef?.current;
    if (isFullscreen) {
      return {
        left: 0,
        top: 0,
        width: window.innerWidth,
        height: window.innerHeight,
      };
    }
    return container?.getBoundingClientRect() ?? {
      left: 0,
      top: 0,
      width: window.innerWidth,
      height: window.innerHeight,
    };
  };

  const resize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    dimensions.current = getBounds();
    canvas.width = dimensions.current.width;
    canvas.height = dimensions.current.height;
  };


  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      stopAnimation();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={
        `pointer-events-none z-50 bg-transparent ${isFullscreen ?
          'fixed top-0 left-0 w-screen h-screen' :
          'absolute top-0 left-0 w-full h-full'}`
      }
    />
  );
});

export default MeteorShowerCanvas;
